import { Fragment, useRef } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { MapIcon, ShoppingBagIcon } from '@heroicons/react/20/solid';
import UserPanel from 'components/UserPanel';
import { ControlContext } from 'provider/ControlProvider';
import { useContext, useEffect } from 'react';
import { ReactComponent as Title } from 'assets/svg/title.svg';
import { ReactComponent as Help } from 'assets/svg/help.svg';
import { gsap } from 'gsap';

export default function Header() {
  const { setShowModal, token, setToken, cart, end, setPinInfo } =
    useContext(ControlContext);
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (token === '') {
      setToken(localStorage.getItem('accessToken')?.toString() ?? '');
    }
    if (end) {
      animationHeader();
    }
  }, [end]);
  const animationHeader = () => {
    gsap.to(headerRef.current, {
      y: 0,
      duration: 1,
      ease: 'power2.out',
    });
  };
  return (
    <Popover
      ref={headerRef}
      className='h-20 w-full px-4 md:px-12 absolute top-0 -translate-y-20 z-50 items-center text-white bg-gradient-to-r from-thirdColor to-forthColor'
    >
      <div className='flex justify-between items-center px-4 py-3 sm:px-6 md:space-x-10'>
        <Title className='h-12' />

        <div className='flex-1 flex items-center justify-end space-x-1 md:space-x-3'>
          <button
            className='relative p-3 hover:bg-zinc-700'
            onClick={() =>
              setPinInfo({
                index: 0,
                partNumber: 0,
                pos: [0, 0, 0],
              })
            }
          >
            <MapIcon className='text-white w-8' />
          </button>
          <button
            className='relative p-3 hover:bg-zinc-700'
            onClick={() => setShowModal(4)}
          >
            <ShoppingBagIcon className='text-white w-8' />
            {cart.reduce((acc, curr) => acc + curr.quantity, 0) > 99 ? (
              <div className='rounded-full bg-red-600 text-white w-6 h-6 absolute bottom-1 right-2 text-xs text-center flex justify-center items-center'>
                <p>99+</p>
              </div>
            ) : cart.reduce((acc, curr) => acc + curr.quantity, 0) > 0 ? (
              <div className='rounded-full bg-red-600 text-white w-5 h-5 absolute bottom-1 right-2 text-xs text-center flex justify-center items-center'>
                <p>{cart.reduce((acc, curr) => acc + curr.quantity, 0)}</p>
              </div>
            ) : null}
          </button>
          <button
            className='relative p-3 hover:bg-zinc-700'
            onClick={() => setShowModal(6)}
          >
            <Help className='text-white w-8 h-8' />
          </button>
          {token !== '' ? (
            <UserPanel />
          ) : (
            <div className='hidden md:flex text-sm space-x-2'>
              <button
                className='rounded-full px-4 py-2 text-xs'
                onClick={() => setShowModal(2)}
              >
                Sign In
              </button>
              <button
                className='rounded-full px-4 py-2 text-xs gradiantButton'
                onClick={() => setShowModal(1)}
              >
                Sign Up
              </button>
            </div>
          )}
        </div>
        <div className='-mr-2 -my-2 md:hidden'>
          <Popover.Button className=' rounded-md p-2 inline-flex items-center justify-center hover:bg-forthColor focus:outline-none focus:ring-2 focus:ring-inset '>
            <span className='sr-only'>Open menu</span>
            <Bars3Icon className='h-6 w-6 text-white' aria-hidden='true' />
          </Popover.Button>
        </div>
      </div>

      <Transition
        as={Fragment}
        enter='duration-200 ease-out'
        enterFrom='opacity-0 scale-95'
        enterTo='opacity-100 scale-100'
        leave='duration-100 ease-in'
        leaveFrom='opacity-100 scale-100'
        leaveTo='opacity-0 scale-95'
      >
        <Popover.Panel
          focus
          className='absolute top-0 inset-x-0 transition transform md:hidden'
        >
          <div className=' ring-1 ring-black ring-opacity-5 bg-gradient-to-r from-thirdColor to-forthColor divide-y-2 divide-forthColor'>
            <div className='pt-4 px-5'>
              <div className='flex items-center justify-between'>
                <Title className='h-12' />
                <div className='-mr-2'>
                  <Popover.Button className='rounded-md p-2 inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-inset '>
                    <span className='sr-only'>Close menu</span>
                    <XMarkIcon
                      className='h-6 w-6 text-white'
                      aria-hidden='true'
                    />
                  </Popover.Button>
                </div>
              </div>
              <div className='mt-6'></div>
            </div>
            <div className='py-6 px-5 transition transform'>
              <button
                className='relative p-3 hover:bg-zinc-700 flex justify-start items-center space-x-4 '
                onClick={() =>
                  setPinInfo({
                    index: 0,
                    partNumber: 0,
                    pos: [0, 0, 0],
                  })
                }
              >
                <MapIcon className='text-white w-8' />
                <p>Init Camera Position</p>
              </button>
              <button
                className='relative p-3 hover:bg-zinc-700 flex justify-start items-center space-x-4'
                onClick={() => setShowModal(4)}
              >
                <ShoppingBagIcon className='text-white w-8' />
                <p>Manage Cart</p>
                {cart.reduce((acc, curr) => acc + curr.quantity, 0) > 99 ? (
                  <div className='rounded-full bg-red-600 text-white w-6 h-6 absolute bottom-1 right-2 text-xs text-center flex justify-center items-center'>
                    <p>99+</p>
                  </div>
                ) : cart.reduce((acc, curr) => acc + curr.quantity, 0) > 0 ? (
                  <div className='rounded-full bg-red-600 text-white w-5 h-5 absolute bottom-1 right-2 text-xs text-center flex justify-center items-center'>
                    <p>{cart.reduce((acc, curr) => acc + curr.quantity, 0)}</p>
                  </div>
                ) : null}
              </button>
              <button
                className='relative p-3 hover:bg-zinc-700 flex justify-start items-center space-x-4'
                onClick={() => setShowModal(6)}
              >
                <Help className='text-white w-8 h-8' />
                <p>Help</p>
              </button>
              {token !== '' ? (
                <UserPanel />
              ) : (
                <div className='text-sm flex-row space-y-5'>
                  <button
                    className='rounded-full px-4 py-2 text-xs gradiantButton w-full'
                    onClick={() => setShowModal(1)}
                  >
                    Sign Up
                  </button>
                  <button
                    className='flex justify-center items-center w-full space-x-2'
                    onClick={() => setShowModal(2)}
                  >
                    <p className='text-stone-300'>Existing customer?</p>
                    <p className=' text-base'>Sign In</p>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
